import { useEffect, useRef, RefObject } from 'react';

// Global registry to limit concurrent video playback
const activeVideos = new Set<HTMLVideoElement>();
const MAX_CONCURRENT_VIDEOS = 4;

function manageVideoPlayback(video: HTMLVideoElement, shouldPlay: boolean) {
  if (shouldPlay) {
    // If we're at max capacity, pause the oldest video
    if (activeVideos.size >= MAX_CONCURRENT_VIDEOS) {
      const oldest = activeVideos.values().next().value;
      if (oldest && oldest !== video) {
        oldest.pause();
        activeVideos.delete(oldest);
      }
    }
    
    // Play the video and add to active set
    video.play().catch(() => {
      // Ignore autoplay errors (common on mobile)
    });
    activeVideos.add(video);
  } else {
    // Pause and remove from active set
    video.pause();
    activeVideos.delete(video);
  }
}

/**
 * Hook to manage video visibility and playback.
 * Pauses videos when they scroll out of view and plays them when visible.
 * Also limits the number of concurrent video playbacks to prevent browser crashes.
 */
export function useVideoVisibility(videoRef: RefObject<HTMLVideoElement>) {
  const isIntersecting = useRef(false);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        isIntersecting.current = entry.isIntersecting;
        manageVideoPlayback(video, entry.isIntersecting);
      },
      { 
        threshold: 0.25, // 25% visible triggers play
        rootMargin: '50px' // Start loading slightly before in view
      }
    );

    observer.observe(video);

    // Cleanup on unmount
    return () => {
      observer.disconnect();
      activeVideos.delete(video);
    };
  }, [videoRef]);

  return isIntersecting;
}

/**
 * Hook to create a video ref and manage its visibility.
 * Returns a ref to attach to a video element.
 */
export function useVideoRef() {
  const videoRef = useRef<HTMLVideoElement>(null);
  useVideoVisibility(videoRef);
  return videoRef;
}
